import { RemoteFrameRefreshPayload, RemoteFrameSubmitPayload } from "./types";
import { hasFrameDatasetAttributes, parseBoolean, readEventValue } from "./utils";

/**
 * Finds the delegated trigger target within frame bounds.
 *
 * @param frame - Remote-frame host element.
 * @param event - Incoming DOM event.
 * @param selector - Delegation selector.
 * @returns Matched target element, or `null` when no valid target is found.
 */
export function findDelegatedTarget(
  frame: HTMLElement,
  event: Event,
  selector: string,
  allowDatasetFallback = false
): Element | null {
  if (!(event.target instanceof Element)) return null;

  if (selector) {
    const hit = event.target.closest(selector);
    if (hit && frame.contains(hit)) return hit;
    if (!allowDatasetFallback) return null;
  }

  return findClosestFrameDatasetTarget(frame, event.target);
}

/**
 * Walks up from event target and returns closest element carrying `data-frame-*`.
 *
 * @param frame - Remote-frame host element.
 * @param from - Node where the delegated event originated.
 * @returns Matching dataset-bearing element, or `null` when absent.
 */
function findClosestFrameDatasetTarget(frame: HTMLElement, from: Element): Element | null {
  let node: Element | null = from;
  while (node && frame.contains(node)) {
    if (hasFrameDatasetAttributes(node)) return node;
    node = node.parentElement;
  }

  return null;
}

/**
 * Builds refresh payload overrides from `data-frame-*` attributes.
 *
 * @param target - Trigger element that carries data attributes.
 * @param event - Event used for dynamic `__value__` extraction.
 * @returns Refresh payload overrides derived from the trigger target.
 */
export function extractPayloadFromTriggerTarget(
  target: Element,
  event: Event
): RemoteFrameRefreshPayload {
  const payload: RemoteFrameRefreshPayload = {};

  const setString = (attr: string, key: keyof RemoteFrameRefreshPayload): void => {
    const value = target.getAttribute(attr);
    if (value === null) return;
    (payload[key] as unknown) = value;
  };

  setString("data-frame-url", "src");
  setString("data-frame-selector", "selector");
  setString("data-frame-source-selector", "sourceSelector");
  setString("data-frame-section-id", "sectionId");
  setString("data-frame-cache", "cache");
  setString("data-frame-history", "history");
  setString("data-frame-history-scope", "historyScope");
  setString("data-frame-select", "select");
  setString("data-frame-query", "query");
  setString("data-frame-url-base", "urlBase");
  setString("data-frame-preserve-state", "preserveState");
  setString("data-frame-target", "target");

  const stopOnHandled = target.getAttribute("data-frame-stop-on-handled");
  if (stopOnHandled !== null) {
    payload.stopOnHandled = parseBoolean(stopOnHandled);
  }

  const shopify = target.getAttribute("data-frame-shopify");
  if (shopify !== null) {
    payload.shopify = parseBoolean(shopify);
  }

  const queryParams: Record<string, string> = {};
  for (let i = 0; i < target.attributes.length; i++) {
    const attr = target.attributes[i];
    if (!attr.name.startsWith("data-frame-query-")) continue;
    const key = attr.name.slice("data-frame-query-".length);
    if (!key) continue;

    const raw = attr.value;
    if (raw === "__value__") {
      queryParams[key] = readEventValue(event, target);
    } else {
      queryParams[key] = raw;
    }
  }

  if (Object.keys(queryParams).length) {
    payload.query = queryParams;
  }

  return payload;
}

/**
 * Evaluates whether default browser behavior should be prevented for a trigger.
 *
 * @param target - Trigger element.
 * @param event - Incoming DOM event.
 * @returns `true` when the trigger opts into prevent-default behavior.
 */
export function shouldPreventDefaultTrigger(target: Element, event: Event): boolean {
  if (!event.cancelable) return false;

  const preventDefault = target.getAttribute("data-frame-prevent-default");
  if (preventDefault === null) return false;
  return parseBoolean(preventDefault);
}

/**
 * Merges submitter-level payload overrides on top of form-level values.
 *
 * @param form - Submitted form element.
 * @param submitter - Optional submitter element.
 * @param event - Submit event.
 * @returns Merged refresh payload.
 */
export function extractPayloadFromSubmitTargets(
  form: HTMLFormElement,
  submitter: Element | null,
  event: Event
): RemoteFrameRefreshPayload {
  let payload = extractPayloadFromTriggerTarget(form, event);

  if (submitter) {
    payload = mergeRefreshPayload(payload, extractPayloadFromTriggerTarget(submitter, event));
  }

  return payload;
}

/**
 * Parses async submit behavior from form and submitter attributes.
 *
 * @param form - Submitted form element.
 * @param submitter - Optional submitter element.
 * @returns Parsed submit payload, or `null` when async mode is disabled.
 */
export function extractSubmitPayload(
  form: HTMLFormElement,
  submitter: Element | null
): RemoteFrameSubmitPayload | null {
  const modeRaw = readSubmitAttr("data-frame-submit", form, submitter).toLowerCase();
  const modeTokens = splitModeTokens(modeRaw);
  const isAsyncMode =
    modeTokens.includes("async") || modeRaw === "true" || modeRaw === "1" || modeRaw === "yes";
  if (!isAsyncMode) {
    return null;
  }

  const refreshRaw = readSubmitAttr("data-frame-submit-refresh", form, submitter).toLowerCase();
  const refresh: RemoteFrameSubmitPayload["refresh"] =
    refreshRaw === "response" || refreshRaw === "none" ? refreshRaw : "network";
  const debounceMs = modeTokens.includes("immediate") ? 0 : 120;

  return {
    mode: "async",
    refresh,
    debounceMs,
  };
}

/**
 * Reads a submit-related attribute with submitter precedence.
 *
 * @param attr - Attribute name to read.
 * @param form - Submitted form element.
 * @param submitter - Optional submitter element.
 * @returns Resolved attribute value.
 */
function readSubmitAttr(attr: string, form: HTMLFormElement, submitter: Element | null): string {
  const submitterValue = submitter?.getAttribute(attr);
  if (submitterValue !== null && submitterValue !== undefined) {
    return submitterValue;
  }

  const formValue = form.getAttribute(attr);
  if (formValue !== null) {
    return formValue;
  }

  return "";
}

/**
 * Splits a submit mode string into normalized option tokens.
 *
 * @param value - Raw mode value.
 * @returns Parsed mode tokens.
 */
function splitModeTokens(value: string): string[] {
  if (!value) return [];
  return value
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean);
}

/**
 * Merges refresh payloads while deep-merging object query entries.
 *
 * @param base - Base payload.
 * @param override - Override payload.
 * @returns Merged payload.
 */
function mergeRefreshPayload(
  base: RemoteFrameRefreshPayload,
  override: RemoteFrameRefreshPayload
): RemoteFrameRefreshPayload {
  const merged: RemoteFrameRefreshPayload = {
    ...base,
    ...override,
  };

  if (
    isQueryObject(base.query) &&
    isQueryObject(override.query)
  ) {
    merged.query = {
      ...base.query,
      ...override.query,
    };
  }

  return merged;
}

/**
 * Type guard for object-style query payloads.
 *
 * @param query - Query payload candidate.
 * @returns `true` when payload is an object query map.
 */
function isQueryObject(
  query: RemoteFrameRefreshPayload["query"]
): query is Record<string, string | number | boolean | null | undefined> {
  return Boolean(query) && typeof query === "object" && !Array.isArray(query);
}
